/* W9.1 — Mémoire décisionnelle organisationnelle : « Qu'avons-nous décidé sur X ? »
 * Réponses fondées UNIQUEMENT sur les snapshots journalisés — sinon NO_RECORDED_HISTORY. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { BrainCircuit, Search, Loader2, CalendarIcon, X } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

function DateField({ value, onChange, label, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="noam-input text-[10px] w-32 flex items-center gap-1.5 text-left" data-testid={testId}>
          <CalendarIcon className="w-3 h-3 text-zinc-500 shrink-0" />
          <span className={value ? "text-zinc-200 flex-1" : "text-zinc-500 flex-1"}>{value ? format(value, "dd.MM.yyyy", { locale: fr }) : label}</span>
          {value && <X className="w-3 h-3 text-zinc-500 hover:text-zinc-300" onClick={(e) => { e.stopPropagation(); onChange(null); }} />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={(d) => { onChange(d || null); setOpen(false); }} locale={fr} />
      </PopoverContent>
    </Popover>
  );
}

const fmtCHF = (v) => v == null ? "—" : new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v);
const SYSTEMS = ["", "deal", "financial_markets", "governance", "platform"];
const VERDICTS = ["", "PURSUE", "HOLD", "PASS"];
const VERDICT_STYLE = { PURSUE: "border-[#1D9E75]/50 text-[#1D9E75]", HOLD: "border-[#EF9F27]/50 text-[#EF9F27]", PASS: "border-[#E24B4A]/50 text-[#E24B4A]" };

export function DecisionMemory() {
  const [q, setQ] = useState("");
  const [system, setSystem] = useState("");
  const [verdict, setVerdict] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setBusy(true);
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set("q", q.trim());
      if (system) p.set("system", system);
      if (verdict) p.set("verdict", verdict);
      if (dateFrom) p.set("date_from", format(dateFrom, "yyyy-MM-dd"));
      if (dateTo) p.set("date_to", format(dateTo, "yyyy-MM-dd"));
      const r = await api.get(`/workspace/decisions/memory?${p.toString()}`);
      setResult(r.data);
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="decision-memory">
      <div className="text-[10px] px-2.5 py-1.5 rounded border border-white/10 text-zinc-400 bg-white/[0.02] flex items-center gap-1.5">
        <BrainCircuit className="w-3.5 h-3.5 text-[#7F77DD]" /> Mémoire décisionnelle : réponses fondées exclusivement sur les faits et snapshots journalisés. Si l'historique est insuffisant, NOAM le dit.
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Qu'avons-nous décidé sur… (titre, question, justification, scénarios)" className="noam-input text-xs flex-1 min-w-[240px]" data-testid="memory-query" />
        <select value={system} onChange={(e) => setSystem(e.target.value)} className="noam-input text-[10px] w-40" data-testid="memory-system">
          {SYSTEMS.map((s) => <option key={s} value={s}>{s || "Tous les systems"}</option>)}
        </select>
        <select value={verdict} onChange={(e) => setVerdict(e.target.value)} className="noam-input text-[10px] w-32" data-testid="memory-verdict">
          {VERDICTS.map((v) => <option key={v} value={v}>{v || "Tous verdicts"}</option>)}
        </select>
        <DateField value={dateFrom} onChange={setDateFrom} label="Depuis…" testId="memory-date-from" />
        <DateField value={dateTo} onChange={setDateTo} label="Jusqu'à…" testId="memory-date-to" />
        <button onClick={search} disabled={busy} className="btn-primary text-xs" data-testid="memory-search">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Interroger
        </button>
      </div>
      {result && result.answer_status === "NO_RECORDED_HISTORY" && (
        <div className="p-6 text-center text-xs border border-dashed border-[#EF9F27]/30 text-[#EF9F27] rounded-lg" data-testid="memory-no-history">
          {result.answer}
        </div>
      )}
      {result && result.answer_status === "OK" && (
        <>
          <div className="noam-card p-3 text-xs text-zinc-300" data-testid="memory-answer">{result.answer}</div>
          <div className="flex gap-4 text-[10px] text-zinc-500 flex-wrap">
            <span>Période couverte : {new Date(result.facts.first_at).toLocaleDateString("fr-CH")} → {new Date(result.facts.last_at).toLocaleDateString("fr-CH")}</span>
            {Object.entries(result.facts.by_system).map(([k, v]) => <span key={k}>{k} <b className="text-zinc-300">{v}</b></span>)}
            {result.facts.realized_payoff_total_chf != null && <span>Réalisé total <b className="text-zinc-300">{fmtCHF(result.facts.realized_payoff_total_chf)}</b></span>}
          </div>
          <div className="space-y-1.5">
            {result.items.map((d) => {
              const dealRef = (d.ref || {}).ref_type === "deal" ? d.ref.ref_id : null;
              const row = (
                <div className="noam-card p-3 text-xs space-y-1 hover:bg-white/[0.03] transition-colors" data-testid={`memory-item-${d.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-zinc-200 flex-1 truncate">{d.title}</span>
                    <span className="text-[9px] text-zinc-600">{d.system} · v{d.estimation_version}</span>
                    {d.verdict && <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${VERDICT_STYLE[d.verdict]}`}>{d.verdict}</span>}
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-400">{d.status}</span>
                  </div>
                  <div className="flex gap-3 text-[10px] text-zinc-500 flex-wrap">
                    <span>{new Date(d.created_at).toLocaleDateString("fr-CH")}</span>
                    {d.adjusted_ev_at_decision_chf != null && <span>AdjEV à la décision <b className="text-zinc-300">{fmtCHF(d.adjusted_ev_at_decision_chf)}</b></span>}
                    {d.payoff_realized_chf != null && <span>Réalisé <b className="text-zinc-300">{fmtCHF(d.payoff_realized_chf)}</b></span>}
                    {d.decided_by_email && <span>décidé par {d.decided_by_email}</span>}
                  </div>
                  {d.rationale && <div className="text-[10px] text-zinc-500 italic truncate">« {d.rationale} »</div>}
                </div>
              );
              return dealRef ? <Link key={d.id} to={`/app/deals/${dealRef}`} className="block">{row}</Link> : <div key={d.id}>{row}</div>;
            })}
          </div>
        </>
      )}
      {!result && (
        <div className="p-8 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg" data-testid="memory-idle">
          Interrogez la mémoire décisionnelle de l'organisation — mots-clés, system, verdict, période.
        </div>
      )}
    </div>
  );
}
